/**
 * What Discard is allowed to do in the mapping workspace. Reverting means
 * returning to the setup a read-back actually proved, so every decision is made
 * against the frozen verified baseline — never against whichever profile the
 * selector happens to show at the moment the user changes their mind.
 */

import {
  resolveVerifiedBaselineRestore,
  type SetupDocumentIdentity,
  type VerifiedBaselineRestore,
  type VerifiedSetupBaseline,
} from '../../../shared/lib/setupRevision';

export interface VerifiedBaselineDiscardPorts {
  /** The verified record the workspace may revert to, already resolved. */
  restore: VerifiedBaselineRestore;
  /** Reloads a profile by its authoritative key; `false` when it could not be loaded. */
  loadProfile: (profileKey: string) => Promise<boolean>;
  /** Whether the workspace still holds unsaved edits, read after the reload. */
  isDirty: () => boolean;
}

/**
 * The baseline the workspace may revert to for the document it is looking at.
 * A document that cannot be identified is not evidence of the same document, so
 * the shared resolver refuses it rather than assuming.
 */
export const resolveWorkspaceBaselineRestore = (
  baseline: VerifiedSetupBaseline | null,
  identity: SetupDocumentIdentity | null
): VerifiedBaselineRestore => resolveVerifiedBaselineRestore(baseline, { identity });

/** Only a restorable baseline lets the guard offer Discard at all. */
export const canDiscardToVerifiedBaseline = (restore: VerifiedBaselineRestore): boolean =>
  restore.kind === 'restore';

/**
 * Reverts to the verified baseline and reports whether the workspace really
 * ended up on it. A refused baseline, a failed reload, or edits that survived
 * the reload all mean the revert did not happen, so the caller keeps the guard
 * up and the user keeps their edits.
 */
export const discardToVerifiedBaseline = async (
  ports: VerifiedBaselineDiscardPorts
): Promise<boolean> => {
  if (ports.restore.kind !== 'restore') {
    return false;
  }

  const reloaded = await ports.loadProfile(ports.restore.profileKey);
  if (!reloaded) {
    return false;
  }

  return !ports.isDirty();
};
